import React from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Box, Button, Flex, Hide, Text } from 'pcln-design-system';
import Badges from './Badges';
import CardContainer from './CardContainer';
import Logo from './Logo';
import ProductIcon from '../ProductIcon/ProductIcon';
import { PRODUCT_ICONS } from '../constants/productIcons';
const CONDENSED_HEIGHT = 232;
const CONDENSED_WIDTH = 324;
const FULL_HEIGHTS = {
  sm: 240,
  lg: 136,
  xl: 296
};
const FULL_WIDTH = 360;
const ICON_SIZE_LARGE = '40px';
const ICON_SIZE_SMALL = '32px';
const LOGO_WIDTHS = {
  sm: 80,
  md: 184,
  lg: 224
};
const CardButton = /*#__PURE__*/styled(Button).withConfig({
  displayName: "MediaImageCard__CardButton",
  componentId: "sc-621ly7-0"
})(["white-space:nowrap;"]);
const OuterBox = /*#__PURE__*/styled(Box).withConfig({
  displayName: "MediaImageCard__OuterBox",
  componentId: "sc-621ly7-1"
})(["box-shadow:", ";", ""], themeGet('shadows.md'), props => props.url ? `
    transition: box-shadow ${themeGet('timingFunctions.easeInOut')(props)} ${themeGet('duration.normal')(props)};
    &:hover {
      box-shadow: ${themeGet('shadows.xl')(props)};
    }
  ` : '');
const ShadowFlex = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "MediaImageCard__ShadowFlex",
  componentId: "sc-621ly7-2"
})(["", ";"], props => props.showGradient ? 'background-image: linear-gradient(to top, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.01) 100%)' : '');
/** @public */
function MediaImageCard({
  backgroundColor,
  backgroundImageUrl,
  backgroundImageAltText,
  badgeColor,
  badgeText,
  borderText,
  callToAction,
  callToActionColor,
  contentAlign = 'default',
  fixedLogoSize,
  heading,
  height,
  icon,
  isAlwaysVertical = false,
  isBadgePositionAbsolute = false,
  isCondensed = false,
  lazyLoad,
  logoImageUrl,
  logoImageAltText,
  minHeight,
  showBorder = false,
  showLogoImageBackground = true,
  subheading,
  targetDate,
  textColor = 'text.lightest',
  timerCopy,
  width
}) {
  const {
    ariaLabel,
    contentTypeId,
    label,
    openInNewTab,
    url,
    onClick
  } = callToAction || {};
  let buttonColor = callToActionColor || 'primary';
  if (callToActionColor && callToActionColor.includes('.')) {
    buttonColor = callToActionColor.substring(0, callToActionColor.indexOf('.'));
  }
  const cardWidth = width ? width : isCondensed ? [1, null, CONDENSED_WIDTH] : [1, null, FULL_WIDTH];
  const cardHeight = height ? height : isCondensed ? CONDENSED_HEIGHT : FULL_HEIGHTS.xl;
  const cardMinHeight = minHeight ? minHeight : isAlwaysVertical ? [FULL_HEIGHTS.sm, null, null, null, cardHeight] : [FULL_HEIGHTS.sm, null, FULL_HEIGHTS.lg, null, cardHeight];
  const hasBackgroundImage = backgroundImageUrl && typeof backgroundImageUrl === 'string';
  const hasBadges = Boolean(badgeText || borderText);
  const buttonVariation = hasBackgroundImage ? 'fill' : 'white';
  const isButtonCta = contentTypeId === 'button';
  const isTransparent = !backgroundImageUrl && !backgroundColor;
  const isContrastEnabled = (isTransparent || hasBackgroundImage) && Boolean(heading || subheading);
  const isCenterAligned = contentAlign === 'center';
  const fullWidthHideProps = isAlwaysVertical ? {
    xs: true,
    sm: true,
    md: true,
    lg: true,
    xl: true,
    xxl: true
  } : {
    xs: true,
    sm: true,
    xl: true,
    xxl: true
  };
  const cardProps = {
    ariaLabel,
    backgroundImageUrl,
    backgroundImageAltText,
    borderRadius: 'xl',
    color: hasBackgroundImage ? undefined : backgroundColor,
    openInNewTab,
    p: ['12px', null, null, null, 3],
    url,
    onClick,
    ...(showBorder ? {
      borderColor: 'background.lightest',
      borderStyle: 'solid',
      borderWidth: 4
    } : {
      borderWidth: 0
    })
  };
  const textProps = {
    textAlign: isCenterAligned ? isAlwaysVertical ? 'center' : ['center', null, 'left', null, 'center'] : 'left',
    color: textColor,
    enableTextShadow: isContrastEnabled,
    textShadowSize: 'sm'
  };
  const showIcon = icon && PRODUCT_ICONS.includes(icon);
  const iconNode = showIcon ? React.createElement(Flex, {
    justifyContent: isCenterAligned ? 'center' : 'right',
    width: isCenterAligned ? 1 : undefined
  }, React.createElement(ProductIcon, {
    type: icon,
    backgroundColor: 'primary.light',
    height: [ICON_SIZE_SMALL, null, null, null, ICON_SIZE_LARGE],
    width: [ICON_SIZE_SMALL, null, null, null, ICON_SIZE_LARGE]
  })) : null;
  return React.createElement(Flex, {
    width: 1
  }, React.createElement(Hide, {
    md: !isAlwaysVertical,
    lg: !isAlwaysVertical,
    width: 1
  }, React.createElement(OuterBox, {
    borderRadius: 'xl',
    minHeight: cardMinHeight,
    url: url,
    width: cardWidth
  }, React.createElement(CardContainer, {
    ...cardProps,
    flexDirection: 'column',
    minHeight: cardMinHeight,
    showGradient: isContrastEnabled
  }, React.createElement(Flex, {
    flexDirection: 'column',
    width: 1
  }, React.createElement(Box, {
    style: {
      position: isBadgePositionAbsolute ? 'absolute' : undefined
    }
  }, React.createElement(Badges, {
    badgeColor: badgeColor,
    badgeText: badgeText,
    borderText: borderText,
    textColor: textColor,
    targetDate: targetDate,
    timerCopy: timerCopy
  })), logoImageUrl && React.createElement(Flex, {
    justifyContent: 'center'
  }, React.createElement(Logo, {
    isCondensed: isCondensed,
    fixedLogoSize: fixedLogoSize,
    logoImageUrl: logoImageUrl,
    logoImageAltText: logoImageAltText,
    lazyLoad: lazyLoad,
    showLogoImageBackground: showLogoImageBackground,
    p: 2,
    width: isCondensed ? LOGO_WIDTHS.md : [LOGO_WIDTHS.md, null, LOGO_WIDTHS.lg]
  }))), React.createElement(Flex, {
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: '100%'
  }, isCenterAligned && iconNode, React.createElement(Flex, {
    alignItems: 'center',
    justifyContent: isCenterAligned ? 'center' : 'space-between',
    mt: 2,
    width: '100%'
  }, React.createElement(Flex, null, React.createElement(Flex, {
    alignItems: isCenterAligned ? 'center' : 'baseline',
    flexDirection: 'column',
    pr: isButtonCta || showIcon ? 1 : undefined
  }, React.createElement(Text, {
    textStyle: isCondensed ? 'heading5' : ['heading5', null, null, null, 'heading4'],
    ...textProps
  }, heading), React.createElement(Text, {
    textStyle: isCondensed ? 'subheading6' : ['subheading6', null, null, null, 'subheading5'],
    mt: 1,
    ...textProps
  }, subheading), React.createElement(Flex, {
    alignItems: 'center',
    justifyContent: isCenterAligned ? 'center' : undefined,
    mt: [1, null, null, null, 2],
    pt: 2
  }, isButtonCta && React.createElement(CardButton, {
    "aria-label": ariaLabel,
    color: buttonColor,
    size: 'small',
    variation: buttonVariation
  }, label)))), !isCenterAligned && iconNode))))), React.createElement(Hide, {
    ...fullWidthHideProps,
    width: 1
  }, React.createElement(Box, {
    borderRadius: 'xl',
    height: '100%',
    minHeight: cardMinHeight,
    width: 1
  }, React.createElement(CardContainer, {
    ...cardProps,
    minHeight: cardMinHeight,
    p: '12px'
  }, (logoImageUrl || badgeText || borderText) && React.createElement(Flex, {
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: ['auto', 224],
    pr: [1, null, 2],
    mr: 1
  }, React.createElement(Badges, {
    badgeColor: badgeColor,
    badgeText: badgeText,
    borderText: borderText,
    pb: '12px',
    targetDate: targetDate,
    textColor: textColor,
    timerCopy: timerCopy
  }), logoImageUrl && React.createElement(Flex, {
    alignItems: hasBadges ? 'flex-end' : undefined,
    height: '100%',
    justifyContent: 'flex-start'
  }, React.createElement(Logo, {
    fixedLogoSize: fixedLogoSize,
    lazyLoad: lazyLoad,
    isCondensed: hasBadges,
    logoImageUrl: logoImageUrl,
    logoImageAltText: logoImageAltText,
    minHeight: hasBadges ? showBorder ? 70 : 78 : undefined,
    px: 0,
    showLogoImageBackground: showLogoImageBackground,
    width: hasBadges ? [LOGO_WIDTHS.sm, LOGO_WIDTHS.md, LOGO_WIDTHS.lg] : LOGO_WIDTHS.lg
  }))), React.createElement(ShadowFlex, {
    alignItems: 'center',
    borderRadius: 'lg',
    flexDirection: 'column',
    minHeight: '100%',
    justifyContent: 'flex-end',
    showGradient: isContrastEnabled,
    width: 1
  }, React.createElement(Flex, {
    alignItems: 'flex-end',
    flexDirection: 'row',
    justifyContent: 'space-between',
    p: '12px',
    width: '100%'
  }, React.createElement(Flex, null, React.createElement(Flex, {
    flexDirection: 'column'
  }, React.createElement(Text, {
    textStyle: 'heading5',
    ...textProps
  }, heading), React.createElement(Text, {
    textStyle: 'subheading6',
    mt: 1,
    ...textProps
  }, subheading))), React.createElement(Flex, {
    alignItems: isCenterAligned ? 'center' : 'flex-start',
    flexDirection: 'column',
    width: 'unset'
  }, React.createElement(Flex, {
    alignItems: 'center',
    pt: 1,
    ml: 1
  }, isButtonCta && React.createElement(Box, null, React.createElement(CardButton, {
    "aria-label": ariaLabel,
    color: buttonColor,
    size: 'small',
    variation: buttonVariation
  }, label)), showIcon && React.createElement(Box, {
    ml: 1,
    pl: 2
  }, iconNode)))))))));
}
export default MediaImageCard;